import type { StatementView } from '../../supplier-statement.js';

/**
 * Tedarikçi kartının "Account & history" penceresi (ADR 0119): API biçimleri. Tutarlar kanonik
 * ondalık metindir (`"1200.50"`); biçimlendirme `formatDecimal`'dadır.
 */

export type InvoiceStatus = 'pending' | 'approved' | 'rejected';
export type InvoiceSource = 'portal' | 'office';
export type PaymentStatus = 'pending' | 'approved' | 'rejected';
export type MatchWarning = 'OVER_PO' | 'OVER_RECEIVED' | 'NOT_RECEIVED';

export interface CurrencyBalanceView {
  currency: string;
  invoiced: string;
  paid: string;
  balance: string;
  pendingInvoices: string;
  pendingPayments: string;
}

export interface AgingView {
  currency: string;
  buckets: { notDue: string; d1_30: string; d31_60: string; d61_90: string; d90plus: string };
  overdue: string;
  unapplied: string;
  items: Array<{
    id: string;
    invoiceNo: string;
    dueDate: string;
    total: string;
    outstanding: string;
    daysOverdue: number;
  }>;
}

export interface MatchView {
  poTotal: string;
  received: string;
  invoiced: string;
  approved: string;
  remaining: string;
  warnings: MatchWarning[];
}

export interface OfficeInvoiceView {
  id: string;
  invoiceNo: string;
  issueDate: string;
  dueDate: string;
  currency: string;
  net: string;
  tax: string;
  total: string;
  taxLines: Array<{ label: string; rate: string; amount: string }>;
  status: InvoiceStatus;
  reviewNote: string | null;
  overBillingReason: string | null;
  replacesInvoiceId: string | null;
  fileName: string;
  fileSize: number;
  fileSha256: string;
  submittedAt: string;
  poNo: string | null;
  purchaseOrderId: string;
  /** Ödülsüz (eski) PO'da `null` (ADR 0119 Ek 2026-10-24). */
  awardId: string | null;
  /** `office`: portalı kullanmayan tedarikçi adına ofis girdi. */
  source: InvoiceSource;
  /** Ofis girişinde giren kişi; o onaylayamaz (dört göz). */
  enteredBy: { userId: string; userName: string | null } | null;
  contentHash: string;
  match: MatchView | null;
  review: {
    action: string;
    note: string | null;
    warnings: MatchWarning[];
    warningsAcknowledged: boolean;
    userName: string | null;
    at: string;
  } | null;
}

export interface OfficePaymentView {
  id: string;
  kind: 'payment' | 'reversal';
  currency: string;
  amount: string;
  valueDate: string;
  reference: string;
  note: string | null;
  invoiceId: string | null;
  invoiceNo: string | null;
  reversesPaymentId: string | null;
  reversedBy: string | null;
  bank: { bankAccountId: string; name: string | null; masked: string | null } | null;
  receipt: { name: string; size: number } | null;
  status: PaymentStatus;
  contentHash: string;
  recordedBy: { userId: string; userName: string | null };
  recordedAt: string;
  decision: { action: string; note: string | null; userName: string | null; at: string } | null;
}

export interface PaymentBankOption {
  bankAccountId: string;
  bankName: string;
  holderName: string;
  masked: string;
  currency: string;
  isDefault: boolean;
  approvedAt: string | null;
}

export interface SupplierAccountData {
  balances: CurrencyBalanceView[];
  aging: AgingView[];
  invoices: OfficeInvoiceView[];
  payments: OfficePaymentView[];
  banks: PaymentBankOption[];
  can: { record: boolean; review: boolean; files: boolean };
  userId: string;
}

export type RfqOutcome = 'won' | 'lost' | 'cancelled' | 'no_response' | 'pending' | 'open';

export interface SupplierHistoryData {
  requests: Array<{
    id: string;
    rfqNo: string;
    vesselName: string;
    currency: string;
    requestDate: string | null;
    closingAt: string | null;
    invited: boolean;
    quoted: boolean;
    revisions: number;
    lastSubmittedAt: string | null;
    outcome: RfqOutcome;
  }>;
  responseRate: {
    invited: number;
    responded: number;
    rate: number | null;
    openMarketQuotes: number;
  };
  orders: Array<{
    id: string;
    poNo: string;
    orderDate: string | null;
    vesselName: string | null;
    currency: string;
    total: string;
    cancelled: boolean;
  }>;
  invoices: Array<{
    id: string;
    invoiceNo: string;
    issueDate: string;
    currency: string;
    total: string;
    status: InvoiceStatus;
  }>;
  payments: Array<{
    id: string;
    kind: 'payment' | 'reversal';
    valueDate: string;
    currency: string;
    amount: string;
    reference: string;
    invoiceNo: string | null;
  }>;
}

export interface SupplierStatementData extends StatementView {
  currencies: string[];
}

export interface PaymentInputView {
  bankAccountId: string;
  currency: string;
  amount: string;
  valueDate: string;
  reference: string;
  note: string | null;
  invoiceId: string | null;
  receipt: File;
}

/** Ofisin fatura girebildiği PO (ödüllü ya da ödülsüz eski PO). */
export interface OfficeInvoiceTargetView {
  purchaseOrderId: string;
  awardId: string | null;
  poNo: string;
  orderDate: string | null;
  currency: string;
  total: string;
  invoiced: string;
  approved: string;
  remaining: string;
}

/** Ofisin tedarikçi adına girdiği fatura (alanlar portal faturasıyla aynı). */
export interface OfficeInvoiceInputView {
  purchaseOrderId: string;
  invoice: {
    invoiceNo: string;
    issueDate: string;
    dueDate: string;
    currency: string;
    netAmount: string;
    taxLines: Array<{ label: string; rate: string; amount: string }>;
    totalAmount: string;
    overBillingReason: string | null;
    replacesInvoiceId: string | null;
  };
  file: File;
}

/** Zilde karar bekleyen cari kayıtları (ADR 0119; `GET /api/office/supplier-ledger/pending`). */
export interface PendingLedgerInvoice {
  id: string;
  source: InvoiceSource;
  supplierId: string;
  supplierName: string;
  invoiceNo: string;
  poNo: string | null;
  currency: string;
  total: string;
  at: string;
}
export interface PendingLedgerPayment {
  id: string;
  kind: 'payment' | 'reversal';
  supplierId: string;
  supplierName: string;
  currency: string;
  amount: string;
  reference: string;
  at: string;
}
export interface PendingLedger {
  invoices: PendingLedgerInvoice[];
  payments: PendingLedgerPayment[];
}

/** Zil maddesinin açtığı yol: tedarikçiler sayfasında kartın cari penceresi, ilgili sekmede. */
export function supplierAccountPath(
  supplierId: string,
  tab: 'invoices' | 'payments' = 'invoices',
): string {
  return `/purchasing/suppliers?account=${encodeURIComponent(supplierId)}&tab=${tab}`;
}

export interface SupplierAccountSource {
  get(supplierId: string): Promise<SupplierAccountData>;
  history(supplierId: string): Promise<SupplierHistoryData>;
  statement(
    supplierId: string,
    range: { currency: string; from: string; to: string },
  ): Promise<SupplierStatementData>;
  review(
    invoiceId: string,
    input: {
      action: 'approved' | 'rejected';
      note?: string;
      acknowledgeWarnings?: boolean;
      contentHash: string;
      password?: string;
    },
  ): Promise<SupplierAccountData>;
  pay(supplierId: string, input: PaymentInputView): Promise<SupplierAccountData>;
  /** Ofisin fatura girebildiği PO'lar (ADR 0119 Ek 2026-10-24). */
  invoiceTargets(supplierId: string): Promise<OfficeInvoiceTargetView[]>;
  /** Portalı kullanmayan tedarikçi adına fatura; dört göz: giren onaylayamaz. */
  enterInvoice(supplierId: string, input: OfficeInvoiceInputView): Promise<SupplierAccountData>;
  decide(
    paymentId: string,
    input: {
      action: 'approved' | 'rejected';
      note?: string;
      contentHash: string;
      password?: string;
    },
  ): Promise<SupplierAccountData>;
  reverse(paymentId: string, input: { reason: string }): Promise<SupplierAccountData>;
  /** Fatura PDF'i ve dekont: yeni sekmede açılır (oturumlu adres). */
  invoiceFileUrl(invoiceId: string): string;
  receiptUrl(paymentId: string): string;
  /** Ekstre belgesindeki alıcı adı (şirket markası). */
  companyName(): Promise<string>;
  /** Dosyayı kullanıcıya verir (tarayıcı indirmesi). */
  download(fileName: string, bytes: Uint8Array, type: string): Promise<void> | void;
}
